import { create } from 'zustand';
import {
  uploadVideo,
  addVideoToGroup,
  reorderVideoGroup,
  type UploadProgress,
} from '../api/videos';
import { queryClient } from '../lib/queryClient';
import { videoKeys, groupKeys } from '../hooks/useVideos';

// How many uploads run at once. Kept at 1 (serial): the upload handler buffers
// the whole file (io.ReadAll) then spawns ffmpeg to normalize, so two concurrent
// uploads multiply RAM + ffmpeg processes on the single host — which 502s near
// the end of the request (verified: serial succeeds, concurrent fails). Serial
// keeps the drop-a-course UX (queue, drop order, background nav) fully intact.
const CONCURRENCY = 1;

export type UploadStatus =
  | 'queued'
  | 'uploading'
  | 'processing'
  | 'done'
  | 'duplicate'
  | 'failed';

export interface UploadItem {
  localId: string;
  file: File;
  title: string;
  groupId: number;
  // Play-order position, assigned at enqueue in drop order — independent of
  // which upload finishes first. Never reassigned by completion.
  position: number;
  status: UploadStatus;
  progress: UploadProgress | null;
  videoId?: number;
  error?: string;
}

// A settled item has a video attached to the group (done or duplicate); its id
// is known and it participates in the persisted order.
function isSettled(item: UploadItem): boolean {
  return item.status === 'done' || item.status === 'duplicate';
}

function isTerminal(item: UploadItem): boolean {
  return isSettled(item) || item.status === 'failed';
}

interface UploadStore {
  items: UploadItem[];
  // The single group a batch is currently targeting. v1 runs one batch at a
  // time (D9), so a drop onto any other group is refused while this is set.
  activeGroupId: number | null;
  // Returns false (and enqueues nothing) if a batch for another group is live.
  enqueue: (files: File[], groupId: number, baseOrder: number) => boolean;
  retry: (localId: string) => void;
  clearSettled: () => void;
}

let idCounter = 0;

function stripExt(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

export const useUploadStore = create<UploadStore>((set, get) => {
  // Persist the group's play order once a batch has quiesced (no uploads still
  // in flight). Reordering per-file would be N writes per N-file batch (D11).
  async function persistOrderIfQuiesced(groupId: number): Promise<void> {
    const items = get().items;
    const active = items.some(
      (i) => i.groupId === groupId && !isTerminal(i)
    );
    if (active) return;

    const settled = items
      .filter((i) => i.groupId === groupId && isSettled(i) && i.videoId != null)
      .sort((a, b) => a.position - b.position)
      .map((i) => i.videoId!);

    // The reorder endpoint wants the FULL ordered id list. The videos already
    // in the group (not part of this batch) keep their relative order ahead of
    // the freshly-uploaded ones because positions were based off baseOrder.
    if (settled.length > 0) {
      try {
        await reorderVideoGroup(groupId, settled);
      } catch {
        // A failed reorder must not strand the batch; the group detail view
        // refetches order from the server regardless.
      }
    }

    queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    queryClient.invalidateQueries({ queryKey: videoKeys.lists() });

    // Batch done: release the active-group lock if nothing else is pending.
    const stillActive = get().items.some((i) => !isTerminal(i));
    if (!stillActive) set({ activeGroupId: null });
  }

  function patch(localId: string, next: Partial<UploadItem>): void {
    set((state) => ({
      items: state.items.map((i) =>
        i.localId === localId ? { ...i, ...next } : i
      ),
    }));
  }

  async function runOne(localId: string): Promise<void> {
    const item = get().items.find((i) => i.localId === localId);
    if (!item) return;

    patch(localId, { status: 'uploading', progress: { loaded: 0, total: item.file.size, percent: 0 }, error: undefined });

    try {
      const result = await uploadVideo({
        file: item.file,
        title: item.title,
        onProgress: (p) => {
          // Two-phase (D4): bytes-sent hitting 100% precedes server-side
          // normalize/probe/poster, so flip to "processing" instead of pinning
          // the bar at a misleading 100%.
          patch(localId, {
            progress: p,
            status: p.percent >= 100 ? 'processing' : 'uploading',
          });
        },
      });

      await addVideoToGroup(item.groupId, result.video.id);
      patch(localId, {
        status: result.duplicate ? 'duplicate' : 'done',
        videoId: result.video.id,
        progress: { loaded: item.file.size, total: item.file.size, percent: 100 },
      });
    } catch (err) {
      // Per-item isolation (D5): mark failed, keep position, leave the batch
      // running. Retry re-enqueues this same item.
      patch(localId, { status: 'failed', error: (err as Error).message });
    }

    await persistOrderIfQuiesced(item.groupId);
    pump();
  }

  // Fill open slots up to the concurrency cap with queued items, oldest first.
  function pump(): void {
    const items = get().items;
    const inFlight = items.filter(
      (i) => i.status === 'uploading' || i.status === 'processing'
    ).length;
    let free = CONCURRENCY - inFlight;
    if (free <= 0) return;

    for (const item of items) {
      if (free <= 0) break;
      if (item.status === 'queued') {
        free--;
        void runOne(item.localId);
      }
    }
  }

  return {
    items: [],
    activeGroupId: null,

    enqueue: (files, groupId, baseOrder) => {
      // One active batch at a time (D9). A drop onto a different group while a
      // batch is live is refused; the caller surfaces the hint.
      const active = get().activeGroupId;
      if (active !== null && active !== groupId) return false;

      if (files.length === 0) return true;

      const existing = get().items.filter((i) => i.groupId === groupId);
      const startPos =
        existing.length > 0
          ? Math.max(baseOrder, ...existing.map((i) => i.position + 1))
          : baseOrder;

      const newItems: UploadItem[] = files.map((file, idx) => ({
        localId: `up-${++idCounter}`,
        file,
        title: stripExt(file.name),
        groupId,
        position: startPos + idx, // drop order, assigned now (D2)
        status: 'queued',
        progress: null,
      }));

      set((state) => ({
        items: [...state.items, ...newItems],
        activeGroupId: groupId,
      }));
      pump();
      return true;
    },

    retry: (localId) => {
      const item = get().items.find((i) => i.localId === localId);
      if (!item || item.status !== 'failed') return;
      // Re-lock the group and requeue in place; position is preserved (D5/D12).
      set((state) => ({
        activeGroupId: item.groupId,
        items: state.items.map((i) =>
          i.localId === localId
            ? { ...i, status: 'queued', error: undefined, progress: null }
            : i
        ),
      }));
      pump();
    },

    clearSettled: () =>
      set((state) => ({
        items: state.items.filter((i) => !isSettled(i)),
      })),
  };
});
